import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './MapChrome.css';

export interface MapShareMenuItem {
    id: string;
    label: string;
    onClick: () => void;
    disabled?: boolean;
    busy?: boolean;
}

interface MapShareMenuProps {
    items: MapShareMenuItem[];
}

const MapShareMenu: React.FC<MapShareMenuProps> = ({ items }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    if (items.length === 0) return null;

    const busyItem = items.find((item) => item.busy);
    const anyBusy = Boolean(busyItem);

    return (
        <div className="map-share-menu" ref={rootRef}>
            <button
                type="button"
                className={`map-chrome-btn map-chrome-btn--accent${anyBusy ? ' is-busy' : ''}`}
                aria-expanded={open}
                aria-haspopup="menu"
                aria-busy={anyBusy}
                disabled={anyBusy}
                onClick={() => {
                    if (anyBusy) return;
                    setOpen((v) => !v);
                }}
            >
                {anyBusy ? (
                    <>
                        <span className="map-share-menu-spinner" aria-hidden="true" />
                        {t('map.capturing', { defaultValue: 'Capturing…' })}
                    </>
                ) : (
                    <>{t('map.share', { defaultValue: 'Share' })} ▾</>
                )}
            </button>
            {open && !anyBusy && (
                <div className="map-share-menu-panel" role="menu">
                    {items.map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            role="menuitem"
                            className="map-share-menu-item"
                            disabled={item.disabled || item.busy}
                            onClick={() => {
                                item.onClick();
                                setOpen(false);
                            }}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MapShareMenu;
